import { z } from 'zod';
import type {
  SubscriptionCatalog,
  SubscriptionConfigOverride,
} from '../../../types/src/subscription';
import { subscriptionCatalog } from './catalog';

const planTierEnum = z.enum(['free', 'plus', 'pro']);
const planSetEnum = z.enum(['individual', 'employer']);
const modelPolicyEnum = z.enum(['sonnet_only', 'sonnet_plus_opus_auto']);
const limitBehaviorEnum = z.enum([
  'none',
  'hard_stop',
  'soft_throttle',
  'slowdown',
]);
const meterSubjectEnum = z.enum([
  'none',
  'individual_user',
  'employer_workspace',
  'employer_seat',
]);

const planPriceSchema = z.object({
  amount: z.number(),
  currency: z.enum(['AED', 'USD']),
  display: z.string(),
  billedAmount: z.number().optional(),
  billedDisplay: z.string().optional(),
});

const customerAllowanceSchema = z.object({
  limit: z.number().int().positive(),
  cadence: z.enum(['day', 'week', 'month']),
  unit: z.enum(['reply', 'run', 'preview', 'report', 'generation', 'autofill']),
  label: z.string().min(1),
});

const planTierDefinitionSchema = z.object({
  tier: planTierEnum,
  label: z.string(),
  shortLabel: z.string(),
  description: z.string(),
  badge: z.string().optional(),
  billing: z.record(
    z.enum(['monthly', 'yearly']),
    z.object({
      prices: z.array(planPriceSchema),
    }),
  ),
  features: z.array(z.string()),
  limits: z.object({
    aiActionsPerDay: z.number().nullable(),
    aiCreditsPerCycle: z.number().nullable().optional(),
    aiCreditsPerDay: z.number().nullable().optional(),
    activeHiringPages: z.number().nullable().optional(),
    activeJobPosts: z.number().nullable().optional(),
  }),
  modelPolicy: modelPolicyEnum,
  limitBehavior: limitBehaviorEnum,
  marketing: z.object({
    eyebrow: z.string().optional(),
    headline: z.string(),
    subheadline: z.string(),
    cta: z.string(),
    highlight: z.string().optional(),
  }),
});

const catalogSchema = z.object({
  version: z.number().int().positive(),
  planSets: z.record(
    planSetEnum,
    z.object({
      id: planSetEnum,
      label: z.string(),
      description: z.string(),
      defaultTier: planTierEnum,
      seatBased: z.boolean(),
      tiers: z.record(planTierEnum, planTierDefinitionSchema),
      workspaceFreePool: z
        .object({
          aiActionsPerDay: z.number().int().min(0),
          eligibleActions: z.array(z.string()),
        })
        .optional(),
    }),
  ),
  features: z.record(
    z.string(),
    z.object({
      key: z.string(),
      planSet: planSetEnum,
      label: z.string(),
      description: z.string(),
      minimumTier: planTierEnum,
      usesWorkspaceTier: z.boolean().optional(),
      requiresPaidSeat: z.boolean().optional(),
    }),
  ),
  aiActions: z.record(
    z.string(),
    z.object({
      key: z.string(),
      label: z.string(),
      description: z.string(),
      planSet: z.enum(['individual', 'employer', 'onboarding']),
      featureKey: z.string().optional(),
      countsTowardDaily: z.boolean(),
      estimatedCredits: z.number().int().min(0).optional(),
      meterSubject: meterSubjectEnum,
      availableToEmployerFreeWorkspace: z.boolean().optional(),
      requiresPaidSeat: z.boolean().optional(),
      premiumOnComplexOnly: z.boolean().optional(),
      customerAllowances: z
        .record(planTierEnum, customerAllowanceSchema)
        .optional(),
      // Kept through an override (a plain z.object drops unknown keys, which
      // would silently lift the capability quotas and the free samples).
      allowPartialCredits: z.boolean().optional(),
      dailyQuota: z.record(planTierEnum, z.number().int().min(0)).optional(),
      includedDaily: z.record(planTierEnum, z.number().int().min(0)).optional(),
      upgradePromptKey: z.string(),
    }),
  ),
  prompts: z.record(
    z.string(),
    z.object({
      key: z.string(),
      title: z.string(),
      message: z.string(),
      ctaLabel: z.string(),
      recommendedPlanSet: planSetEnum,
      recommendedTier: z.enum(['plus', 'pro']),
    }),
  ),
  legacyPlanNormalization: z.record(planTierEnum),
});

const overrideSchema = catalogSchema.deepPartial();

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge<T>(base: T, override: unknown): T {
  if (Array.isArray(base)) {
    return (Array.isArray(override) ? override : base) as T;
  }

  if (!isPlainObject(base) || !isPlainObject(override)) {
    return (override === undefined ? base : override) as T;
  }

  const result: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    result[key] = key in result ? deepMerge(result[key], value) : value;
  }

  return result as T;
}

export function mergeSubscriptionCatalogOverride(
  override?: SubscriptionConfigOverride | null,
  baseCatalog: SubscriptionCatalog = subscriptionCatalog,
) {
  if (!override) {
    return baseCatalog;
  }

  const parsedOverride = overrideSchema.parse(override);
  const merged = deepMerge(baseCatalog, parsedOverride);
  return catalogSchema.parse(merged);
}
